import { useEffect, useState } from "react";

import {
  ListContainer,
  ListContext,
  PAGE_DEFAULT,
  PAGE_SIZE_UNLIMITED,
  Page,
  PageRoute,
  SearchBar,
  Stack,
  buildPath,
  buildTemplatePath,
  pn,
} from "@compass/core";

import { ListFooter } from "components";
import {
  ListFilterBase,
  Permission,
  StudentServiceTypeListDto,
  StudentServiceTypeListFilter,
  StudentServiceTypeListResource,
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import produce from "immer";
import { Outlet } from "react-router-dom";
import ApiService from "services/apiService";
import { StudentServiceTypeFilter } from "../StudentServiceTypeFilter";
import { StudentServiceTypeListPageHeader } from "./StudentServiceTypeListPageHeader";
import { StudentServiceTypeTable } from "./StudentServiceTypeTable";

const routeName = "studentServiceTypes";

export const studentServiceTypeListPageRoute: PageRoute = {
  handle: "Service Types",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const StudentServiceTypeListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.MatchUtility;

  const [listItems, setListItems] = useState<StudentServiceTypeListDto[]>([]);

  const { listFilter, setListFilter } = useInitListFilter<StudentServiceTypeListFilter>({
      sortObjects: [{ property: pn<StudentServiceTypeListDto>("name"), isDesc: false }],
      serviceTypeFilter: {
          inactive: false
      }
  });

  const [initListFilter] = useState<StudentServiceTypeListFilter>(listFilter);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<StudentServiceTypeListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const { tableContainerRef, applyPage, applyPageSize, loadList, applySort, applySearch, applySortMethod } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.studentServiceTypes.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.studentServiceTypes.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);
    ApiService.studentServiceTypes.getListResource().then((response) => {
      setResource(response);
    });
  }, []);

  const listFilterNode = useListFilter<StudentServiceTypeListFilter>({
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <StudentServiceTypeFilter
        filter={listFilter.serviceTypeFilter || {}}
        setFilter={(filter) =>
          setListFilter(
            produce(listFilter, (draft) => {
              draft.serviceTypeFilter = filter;
            })
          )
        }
      />
    ),
  });

  return (
    <>
      <Page
        header={
          <StudentServiceTypeListPageHeader
            rootPermission={ROOT_PERMISSION}
            handle={studentServiceTypeListPageRoute.handle}
          />
        }
      >
        <ListContainer
          fullHeight
          header={
            <Stack direction="row" justifyContent="space-between">
              <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
              {listFilterNode}
            </Stack>
          }
          tableContainer={
            <StudentServiceTypeTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              studentServiceTypes={listItems}
              setStudentServiceTypes={setListItems}
              resource={resource}
              searchFocused={searchFocused}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              loadList={() => loadList(listFilter)}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
